import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, ExternalLink, Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { scrollToSection, useLanguage } from "../i18n";
import { LOGO, LOGO_LIGHT, LINKS, trackEvent } from "../data";

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll + Escape to close + focus management
  useEffect(() => {
    if (!menuOpen) return;
    const prevOverflow = document.body.style.overflow;
    const menuButton = menuButtonRef.current;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const firstLink = drawerRef.current?.querySelector("a, button");
    firstLink?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
      menuButton?.focus();
    };
  }, [menuOpen]);

  const links = [
    { label: t.nav.stays, to: "/strutture", testid: "nav-stays-link" },
    { label: t.nav.villas, to: "/servizi#villas", testid: "nav-villas-link" },
    { label: t.nav.experiences, to: "/esperienze", testid: "nav-experiences-link" },
    { label: t.nav.owners, to: "/servizi", testid: "nav-owners-link" },
    { label: t.nav.metodo, to: "/servizi#metodo", testid: "nav-metodo-link" },
    { label: t.nav.about, to: "/chi-siamo", testid: "nav-about-link" },
  ];

  const go = (link) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    const [path, hash] = link.to.split("#");
    if (location.pathname === path) {
      if (hash) scrollToSection(`#${hash}`);
      else if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.2 });
    } else {
      navigate(link.to);
    }
  };

  const goHome = (e) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
    } else if (window.__lenis) {
      window.__lenis.scrollTo(0, { duration: 1.4 });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const linkCls = scrolled ? "text-ink hover:text-navy" : "text-white/90 hover:text-white";

  const langToggle = (mobile) => (
    <div
      data-testid={mobile ? "mobile-language-toggle" : "language-toggle"}
      role="group"
      aria-label="Language / Lingua"
      className={`flex items-center rounded-full p-1 text-[11px] font-extrabold tracking-widest ring-1 transition-colors duration-500 ${
        mobile || scrolled ? "bg-white ring-navy/15" : "bg-white/10 ring-white/30 backdrop-blur-sm"
      }`}
    >
      {["it", "en"].map((l) => (
        <button
          key={l}
          data-testid={mobile ? `mobile-lang-${l}-button` : `lang-${l}-button`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`rounded-full px-3 py-1.5 uppercase transition-colors duration-300 ${
            lang === l ? "bg-navy text-white" : mobile || scrolled ? "text-mute hover:text-navy" : "text-white/70 hover:text-white"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter,border-color] duration-500 ${
        scrolled || menuOpen
          ? "border-b border-navy/10 bg-warm/80 shadow-[0_10px_40px_-20px_rgba(15,43,58,0.35)] backdrop-blur-xl"
          : "border-b border-transparent bg-gradient-to-b from-navy/50 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between gap-4 px-5 sm:px-8">
        <a href="/" data-testid="header-logo" onClick={goHome} aria-label="OGStay — Home" className="relative flex shrink-0 items-center px-2 py-2">
          <img
            src={LOGO_LIGHT}
            alt="OGStay — Live Ogliastra. Stay Local."
            className={`h-11 w-auto transition-opacity duration-500 ${scrolled || menuOpen ? "opacity-0" : "opacity-100"}`}
          />
          <img
            src={LOGO}
            alt=""
            aria-hidden
            className={`absolute left-2 top-1/2 h-11 w-auto -translate-y-1/2 transition-opacity duration-500 ${scrolled || menuOpen ? "opacity-100" : "opacity-0"}`}
          />
        </a>

        <nav data-testid="main-nav" aria-label={t.nav.menu} className="hidden items-center gap-6 lg:flex">
          {links.map((l) => (
            <a
              key={l.testid}
              href={l.to}
              data-testid={l.testid}
              onClick={go(l)}
              className={`group relative whitespace-nowrap text-[12px] font-bold uppercase tracking-[0.14em] transition-colors duration-500 ${linkCls}`}
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-sky transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-3">
          <div className="hidden sm:block">{langToggle(false)}</div>

          <a
            data-testid="header-structures-cta"
            href={LINKS.booking}
            onClick={() => trackEvent("booking_click", { source: "header" })}
            className="btn-primary hidden !px-6 !py-3 text-[13px] xl:inline-flex"
          >
            <Building2 className="h-4 w-4" />
            {t.nav.structures}
          </a>

          <button
            ref={menuButtonRef}
            data-testid="mobile-menu-button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? t.nav.close : t.nav.menu}
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            className={`inline-flex h-11 w-11 items-center justify-center rounded-full ring-1 transition-colors lg:hidden ${
              scrolled || menuOpen ? "bg-white text-navy ring-navy/15" : "bg-white/10 text-white ring-white/30 backdrop-blur-sm"
            }`}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div
              data-testid="mobile-menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={() => setMenuOpen(false)}
              aria-hidden
              className="fixed inset-0 top-[76px] z-40 bg-navy/40 backdrop-blur-sm lg:hidden"
            />
            <motion.nav
              ref={drawerRef}
              id="mobile-menu"
              data-testid="mobile-menu"
              aria-label={t.nav.menu}
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="fixed inset-x-0 top-[76px] z-50 max-h-[calc(100dvh-76px)] overflow-y-auto border-t border-navy/10 bg-warm shadow-2xl lg:hidden"
            >
              <div className="space-y-1 px-6 py-6">
                {links.map((l) => (
                  <a
                    key={l.testid}
                    href={l.to}
                    data-testid={`mobile-${l.testid}`}
                    onClick={go(l)}
                    className="block rounded-xl px-4 py-3.5 font-display text-2xl text-navy transition-colors hover:bg-sand"
                  >
                    {l.label}
                  </a>
                ))}
                <a
                  data-testid="mobile-nav-portal-link"
                  href={LINKS.ownerPortal}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between rounded-xl px-4 py-3.5 font-display text-2xl italic text-med transition-colors hover:bg-sand"
                >
                  {t.nav.portal}
                  <ExternalLink className="h-5 w-5" />
                </a>
                <div className="flex flex-wrap items-center gap-3 px-4 pt-5">
                  {langToggle(true)}
                  <a
                    data-testid="mobile-structures-cta"
                    href={LINKS.booking}
                    onClick={() => trackEvent("booking_click", { source: "header_mobile" })}
                    className="btn-primary flex-1 !py-3 text-[13px]"
                  >
                    <Building2 className="h-4 w-4" />
                    {t.nav.structures}
                  </a>
                </div>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
